import RestaurantPlaceholder from '@/components/display/restaurant-placeholder';
import Ionicons from '@expo/vector-icons/Ionicons';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Dimensions, Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { HeroTags } from './hero-tags';

const HERO_HEIGHT = 380;

interface HeroImageProps {
  imageUrl?: string | null;
  title: string;
  subtitle?: string;
  tags?: string[];
  placeholderName?: string;
  onBackPress?: () => void;
  onActionPress?: () => void;
  actionIcon?: keyof typeof Ionicons.glyphMap;
  actionLabel?: string;
}

export const HeroImage = React.memo<HeroImageProps>(({
  imageUrl,
  title,
  subtitle,
  tags,
  placeholderName,
  onBackPress,
  onActionPress,
  actionIcon = 'share-outline',
  actionLabel = 'Share',
}) => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [containerWidth, setContainerWidth] = useState(
    () => Dimensions.get('window').width
  );
  const [imageError, setImageError] = useState(false);

  const showPlaceholder = !imageUrl || imageError;

  const handleBack = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      router.back();
    }
  };

  return (
    <View
      style={{ height: HERO_HEIGHT, overflow: 'hidden' }}
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {/* Background: Image or Placeholder */}
      {showPlaceholder ? (
        <View style={StyleSheet.absoluteFill}>
          <RestaurantPlaceholder
            name={placeholderName}
            width={containerWidth}
            height={HERO_HEIGHT}
          />
        </View>
      ) : (
        <Image
          source={{ uri: imageUrl }}
          style={StyleSheet.absoluteFill}
          resizeMode="cover"
          onError={() => setImageError(true)}
          accessibilityLabel={`Cover photo of ${title}`}
        />
      )}

      {/* Gradient overlay -- final color matches dark theme --background (#080706) */}
      <LinearGradient
        colors={['transparent', 'transparent', 'rgba(8,7,6,0.7)', '#080706']}
        locations={[0, 0.35, 0.7, 1.0]}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />

      {/* Top bar: back + action buttons */}
      <View
        style={[
          styles.topBar,
          { paddingTop: insets.top + 8 },
        ]}
      >
        <BlurView intensity={100} tint="dark" style={styles.blurContainer}>
          <Pressable
            onPress={handleBack}
            style={styles.topButton}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={22} color="white" />
          </Pressable>
        </BlurView>

        {onActionPress && (
          <BlurView intensity={100} tint="dark" style={styles.blurContainer}>
            <Pressable
              onPress={onActionPress}
              style={styles.topButton}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={actionLabel}
            >
              <Ionicons name={actionIcon} size={20} color="white" />
            </Pressable>
          </BlurView>
        )}
      </View>

      {/* Text overlay at bottom */}
      <View style={styles.textOverlay}>
        {subtitle && (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        )}
        <Text style={styles.title} numberOfLines={2} accessibilityRole="header">
          {title}
        </Text>
        <HeroTags tags={tags} />
      </View>
    </View>
  );
});

HeroImage.displayName = 'HeroImage';

const styles = StyleSheet.create({
  topBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    zIndex: 2,
  },
  blurContainer: {
    overflow: 'hidden',
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingBottom: 16,
    zIndex: 1,
  },
  subtitle: {
    color: 'rgba(242, 240, 235, 0.6)',
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.6)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  title: {
    color: '#F2F0EB',
    fontSize: 30,
    fontWeight: '700',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
});
